import { Navigate, useSearchParams } from "react-router-dom";

import { useUser } from "@/api/hooks/useUser";
import Spinner from "@/components/common/Spinner";
import Tabs from "@/components/common/Tabs";
import { isDobuEmployee } from "@/utils/userUtils";

import AccountsTab from "./components/AccountsTab";
import BuildingsTab from "./components/BuildingsTab";
import DevelopmentsTab from "./components/DevelopmentsTab";
import UsersTab from "./components/UsersTab";

type TabType = "accounts" | "users" | "buildings" | "developments";

const tabs: { id: TabType; label: string }[] = [
  { id: "users", label: "Users" },
  { id: "accounts", label: "Accounts" },
  { id: "buildings", label: "Buildings" },
  { id: "developments", label: "Developments" },
];

const isValidTab = (tab: string | null): tab is TabType => {
  return tab !== null && tabs.some((t) => t.id === tab);
};

const parseId = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
};

export default function BackofficePage() {
  const user = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const activeTab: TabType = isValidTab(tabParam) ? tabParam : "accounts";

  const selectedAccountId = parseId(searchParams.get("accountId"));
  const selectedUserId = parseId(searchParams.get("userId"));
  const selectedBuildingId = parseId(searchParams.get("buildingId"));
  const selectedDevelopmentId = parseId(searchParams.get("developmentId"));

  const updateParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    }
    setSearchParams(newParams, { replace: false });
  };

  const setActiveTab = (tab: TabType) => {
    updateParams({ tab });
  };

  const setSelectedAccountId = (id: number | null) => {
    updateParams({ accountId: id?.toString() ?? null });
  };

  const setSelectedUserId = (id: number | null) => {
    updateParams({ userId: id?.toString() ?? null });
  };

  const setSelectedBuildingId = (id: number | null) => {
    updateParams({ buildingId: id?.toString() ?? null });
  };

  const setSelectedDevelopmentId = (id: number | null) => {
    updateParams({ developmentId: id?.toString() ?? null });
  };

  const handleSelectAccount = (accountId: number) => {
    updateParams({ tab: "accounts", accountId: accountId.toString() });
  };

  const handleSelectUser = (userId: number) => {
    updateParams({ tab: "users", userId: userId.toString() });
  };

  const handleSelectBuilding = (buildingId: number) => {
    updateParams({ tab: "buildings", buildingId: buildingId.toString() });
  };

  const handleSelectDevelopment = (developmentId: number) => {
    updateParams({ tab: "developments", developmentId: developmentId.toString() });
  };

  if (user && !isDobuEmployee(user)) {
    return <Navigate to="/household" replace />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  return (
    <main className="flex flex-grow flex-col p-6 md:p-12 overflow-y-auto">
      <h1 className="text-2xl font-bold mb-6">Backoffice</h1>

      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} className="mb-6" />

      {activeTab === "accounts" && (
        <AccountsTab
          selectedAccountId={selectedAccountId}
          onSelectedAccountIdChange={setSelectedAccountId}
          onSelectUser={handleSelectUser}
          onSelectBuilding={handleSelectBuilding}
        />
      )}
      {activeTab === "users" && (
        <UsersTab
          selectedUserId={selectedUserId}
          onSelectedUserIdChange={setSelectedUserId}
          onSelectAccount={handleSelectAccount}
          onSelectBuilding={handleSelectBuilding}
          onSelectDevelopment={handleSelectDevelopment}
        />
      )}
      {activeTab === "buildings" && (
        <BuildingsTab
          selectedBuildingId={selectedBuildingId}
          onSelectedBuildingIdChange={setSelectedBuildingId}
          onSelectAccount={handleSelectAccount}
          onSelectDevelopment={handleSelectDevelopment}
        />
      )}
      {activeTab === "developments" && (
        <DevelopmentsTab
          selectedDevelopmentId={selectedDevelopmentId}
          onSelectedDevelopmentIdChange={setSelectedDevelopmentId}
          onSelectAccount={handleSelectAccount}
          onSelectBuilding={handleSelectBuilding}
        />
      )}
    </main>
  );
}
